import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, CheckCircle2, XCircle, CreditCard, ExternalLink, Shield, ArrowRight, Loader2, Settings2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader } from "@/components/ui-bits";

function money(amount, currency) {
  if (amount == null) return "—";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: (currency || "eur").toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount / 100);
}

export default function Billing() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const isOwner = user?.role === "owner";

  const load = async () => {
    const [s, p] = await Promise.all([api.get("/billing/status"), api.get("/billing/plans")]);
    setStatus(s.data);
    setPlans(p.data.plans || []);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load().catch(() => {}); }, []);

  const upgrade = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error"));
      setBusy(null);
    }
  };

  const openPortal = async (flow = "") => {
    setBusy(flow === "cancel" ? "cancel" : "portal");
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: window.location.origin + "/billing",
        flow,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error"));
      setBusy(null);
      setConfirmCancel(false);
    }
  };

  if (!status) {
    return (
      <div data-testid="page-billing" className="max-w-4xl">
        <PageHeader eyebrow={t("billing_label")} title={t("billing_page_title")} />
        <div className="card p-10 flex items-center gap-3 text-[hsl(var(--text-muted))]">
          <Loader2 size={16} className="animate-spin" /> {t("checking")}
        </div>
      </div>
    );
  }

  const statusChip = {
    trial: { label: t("billing_status_trial"), bg: "hsl(var(--primary-soft))", fg: "hsl(var(--primary))" },
    active: { label: t("billing_status_active"), bg: "hsl(var(--success-soft))", fg: "hsl(var(--success))" },
    past_due: { label: t("billing_status_past_due"), bg: "hsl(45 92% 92%)", fg: "hsl(45 92% 40%)" },
    cancelled: { label: t("billing_status_cancelled"), bg: "hsl(var(--danger-soft))", fg: "hsl(var(--danger))" },
    expired: { label: t("billing_status_expired"), bg: "hsl(var(--danger-soft))", fg: "hsl(var(--danger))" },
    unpaid: { label: t("billing_status_unpaid"), bg: "hsl(var(--warning-soft))", fg: "hsl(var(--warning))" },
  }[status.status] || { label: status.status, bg: "hsl(var(--muted))", fg: "hsl(var(--text))" };

  return (
    <div data-testid="page-billing" className="max-w-4xl">
      <PageHeader
        eyebrow={t("billing_label")}
        title={t("billing_page_title")}
        subtitle={isOwner ? t("billing_page_subtitle_owner") : t("billing_page_subtitle_view")}
      />

      {/* Status card */}
      <motion.section
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="card p-6 md:p-8 mb-4"
        data-testid="billing-status-card"
      >
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center shrink-0">
            <Shield size={16} strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display text-[18px] font-bold text-[hsl(var(--text-strong))]">
                {status.status === "trial" ? t("billing_trial_title") : t("billing_current_title")}
              </div>
              <span
                className="text-[11px] font-semibold px-2.5 py-1 rounded-full"
                style={{ background: statusChip.bg, color: statusChip.fg }}
                data-testid="billing-status-chip"
              >{statusChip.label}</span>
            </div>
            {status.status === "trial" && status.trial_days_left != null && (
              <div className="text-[13px] text-[hsl(var(--text-muted))] mt-1">
                {status.trial_days_left >= 0
                  ? t("billing_trial_days_left").replace("{n}", status.trial_days_left)
                  : t("billing_trial_expired_hint")}
              </div>
            )}
            {status.status === "active" && status.plan && (
              <div className="text-[13px] text-[hsl(var(--text-muted))] mt-1">
                {t("billing_plan")}: <b className="text-[hsl(var(--text-strong))]">{t(`plan_${status.plan}`) !== `plan_${status.plan}` ? t(`plan_${status.plan}`) : status.plan}</b>
                {status.current_period_end && (
                  <> · {t("billing_renews_on")} {new Date(status.current_period_end).toLocaleDateString("nl", { day: "numeric", month: "long", year: "numeric" })}</>
                )}
              </div>
            )}
          </div>
          {isOwner && (status.status === "active" || status.status === "past_due") && (
            <button onClick={() => openPortal("")} disabled={busy === "portal"} className="btn btn-ghost" data-testid="billing-portal-btn">
              <ExternalLink size={13} /> {t("billing_manage")}
            </button>
          )}
        </div>
      </motion.section>

      {/* Manage subscription — Owner + active/past_due */}
      {isOwner && (status.status === "active" || status.status === "past_due") && (
        <motion.section
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="card p-6 md:p-8 mb-4"
          data-testid="billing-manage-section"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[hsl(var(--muted))] text-[hsl(var(--text-strong))] grid place-items-center shrink-0">
              <Settings2 size={16} strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-[18px] font-bold text-[hsl(var(--text-strong))]">
                {t("billing_manage_title")}
              </div>
              <div className="text-[13px] text-[hsl(var(--text-muted))] mt-1 leading-relaxed">
                {t("billing_manage_desc")}
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  onClick={() => openPortal("")}
                  disabled={busy === "portal"}
                  className="btn btn-primary"
                  data-testid="billing-manage-btn"
                >
                  <ExternalLink size={13} /> {busy === "portal" ? t("working") : t("billing_open_portal")}
                </button>
                <button
                  onClick={() => setConfirmCancel(true)}
                  disabled={busy === "cancel"}
                  className="btn btn-ghost !text-[hsl(var(--danger))]"
                  data-testid="billing-cancel-btn"
                >
                  <XCircle size={13} /> {t("billing_cancel_subscription")}
                </button>
              </div>
            </div>
          </div>
        </motion.section>
      )}

      {/* Locked banner */}
      {status.locked && (
        <motion.section
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          className="card p-5 mb-4 border-l-[3px] border-l-[hsl(var(--danger))]"
          data-testid="billing-locked-banner"
        >
          <div className="flex items-start gap-3">
            <XCircle size={18} className="text-[hsl(var(--danger))] shrink-0 mt-0.5" />
            <div>
              <div className="font-display text-[15px] font-bold text-[hsl(var(--text-strong))]">{t("billing_locked_title")}</div>
              <div className="text-[13px] text-[hsl(var(--text-muted))] mt-0.5 leading-relaxed">{t("billing_locked_body")}</div>
            </div>
          </div>
        </motion.section>
      )}

      {/* Plans grid — Feb 2026: always visible for owner, even when subscribed.
          Current plan gets a highlight ring + "Huidig"-badge and the CTA
          becomes "Beheren" (opens Stripe portal). Other plans stay
          upgrade/downgrade-able through checkout. */}
      {isOwner && (
        <motion.section
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          data-testid="billing-plans"
        >
          <div className="eyebrow mb-3 flex items-center gap-1.5"><Sparkles size={12} /> {status.status === "active" ? t("billing_all_plans") : t("billing_choose_plan")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {plans.map((p) => {
              const isCurrent = status.status === "active" && status.plan === p.lookup_key;
              const range = p.min_employees && p.max_employees
                ? t("billing_range_employees").replace("{min}", p.min_employees).replace("{max}", p.max_employees)
                : (p.max_employees ? t("billing_up_to_x_employees").replace("{n}", p.max_employees) : t("billing_unlimited_employees"));
              return (
                <motion.div
                  key={p.lookup_key}
                  initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35 }}
                  className={`card p-6 md:p-7 relative overflow-hidden transition-all ${
                    isCurrent
                      ? "ring-2 ring-[hsl(var(--primary))] shadow-[var(--shadow-md)]"
                      : p.lookup_key === "team_monthly"
                        ? "ring-1 ring-[hsl(var(--primary)/0.35)]"
                        : ""
                  }`}
                  style={
                    p.lookup_key === "team_monthly" && !isCurrent
                      ? { background: "linear-gradient(180deg, hsl(var(--primary-soft)/0.35) 0%, hsl(var(--surface)) 55%)" }
                      : undefined
                  }
                  data-testid={`billing-plan-${p.lookup_key}`}
                >
                  {isCurrent ? (
                    <div className="absolute top-4 right-4 chip chip-primary !text-[10px]" data-testid={`billing-plan-current-${p.lookup_key}`}>
                      <CheckCircle2 size={11} strokeWidth={2.2} /> {t("billing_current_plan")}
                    </div>
                  ) : p.lookup_key === "team_monthly" ? (
                    <div className="absolute top-4 right-4 chip chip-primary !text-[10px]">{t("billing_recommended")}</div>
                  ) : null}
                  <div className="font-display text-[22px] font-bold tracking-tight text-[hsl(var(--text-strong))] leading-none">
                    {p.name}
                  </div>
                  <div className="text-[11.5px] font-semibold text-[hsl(var(--primary))] mt-2 leading-snug min-h-[16px]" data-testid={`billing-best-for-${p.lookup_key}`}>
                    {t(`plan_best_for_${p.lookup_key}`) !== `plan_best_for_${p.lookup_key}` ? t(`plan_best_for_${p.lookup_key}`) : ""}
                  </div>
                  <div className="text-[12px] text-[hsl(var(--text-muted))] mt-1 tabular-nums">{range}</div>
                  <div className="mt-5 flex items-baseline gap-1">
                    <div className="font-display text-[38px] leading-none font-bold tabular-nums text-[hsl(var(--text-strong))] tracking-tight">
                      {money(p.amount, p.currency)}
                    </div>
                    <div className="text-[12px] text-[hsl(var(--text-muted))]">/ {t("billing_per_month")}</div>
                  </div>
                  <div className="mt-5 h-px bg-[hsl(var(--border))]" />
                  <ul className="mt-4 flex flex-col gap-2 text-[12.5px] text-[hsl(var(--text))]">
                    {[
                      t(`plan_feat_${p.lookup_key}_1`),
                      t(`plan_feat_${p.lookup_key}_2`),
                      t(`plan_feat_${p.lookup_key}_3`),
                      t(`plan_feat_${p.lookup_key}_4`),
                    ]
                      .filter((f) => f && !f.startsWith("plan_feat_"))
                      .map((f, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 size={13} className="text-[hsl(var(--success))] mt-0.5 shrink-0" />
                          <span className="leading-snug">{f}</span>
                        </li>
                      ))}
                  </ul>
                  {isCurrent ? (
                    <button
                      onClick={() => openPortal("")}
                      disabled={busy === "portal"}
                      className="btn btn-quiet w-full mt-6 !text-[13px]"
                      data-testid={`billing-manage-${p.lookup_key}`}
                    >
                      <Settings2 size={13} strokeWidth={2} /> {busy === "portal" ? t("working") : t("billing_manage")}
                    </button>
                  ) : (
                    <button
                      onClick={() => upgrade(p.lookup_key)}
                      disabled={busy === p.lookup_key}
                      className="btn btn-primary w-full mt-6 !text-[13px]"
                      data-testid={`billing-upgrade-${p.lookup_key}`}
                    >
                      <CreditCard size={13} strokeWidth={2} />
                      {busy === p.lookup_key
                        ? t("working")
                        : (status.status === "active" ? t("billing_switch_to_plan") : t("billing_start_plan"))}
                      <ArrowRight size={13} />
                    </button>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Custom / Enterprise tier — 500+ seats via email */}
          <div
            className="card p-6 md:p-7 mt-4 relative overflow-hidden"
            data-testid="billing-plan-custom"
            style={{ background: "linear-gradient(135deg, hsl(var(--surface)) 0%, hsl(var(--primary-soft)) 100%)" }}
          >
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
              <div className="flex-1 min-w-0">
                <div className="eyebrow mb-1">{t("billing_custom_eyebrow")}</div>
                <div className="font-display text-[22px] font-bold tracking-tight text-[hsl(var(--text-strong))]">
                  {t("billing_custom_title")}
                </div>
                <div className="text-[13px] text-[hsl(var(--text-muted))] mt-1.5 leading-relaxed max-w-xl">
                  {t("billing_custom_desc")}
                </div>
              </div>
              <a
                href="mailto:rosterhelp@gmail.com?subject=Rostr%20—%20Custom%20plan%20aanvraag"
                data-testid="billing-custom-email"
                className="btn btn-primary shrink-0"
              >
                <ExternalLink size={13} strokeWidth={2} /> {t("billing_custom_cta")}
              </a>
            </div>
          </div>
        </motion.section>
      )}

      {!isOwner && (
        <div className="card p-6 text-[13px] text-[hsl(var(--text-muted))]" data-testid="billing-not-owner-hint">
          {t("billing_not_owner_hint")}
        </div>
      )}

      {/* Cancel-subscription confirmation modal */}
      {confirmCancel && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => (busy === "cancel" ? null : setConfirmCancel(false))}
          data-testid="billing-cancel-modal"
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 12 }} animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="card w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "var(--shadow-xl)" }}
          >
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] grid place-items-center shrink-0">
                <AlertTriangle size={18} strokeWidth={2} />
              </div>
              <div>
                <h3 className="font-display text-[19px] font-bold tracking-tight text-[hsl(var(--text-strong))]">
                  {t("billing_cancel_confirm_title")}
                </h3>
                <p className="text-[13px] text-[hsl(var(--text-muted))] mt-1 leading-relaxed">
                  {t("billing_cancel_confirm_body")}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setConfirmCancel(false)}
                disabled={busy === "cancel"}
                className="btn btn-quiet"
                data-testid="billing-cancel-abort"
              >
                {t("cancel")}
              </button>
              <button
                onClick={() => openPortal("cancel")}
                disabled={busy === "cancel"}
                className="btn btn-primary !bg-[hsl(var(--danger))]"
                data-testid="billing-cancel-confirm"
              >
                <XCircle size={13} /> {busy === "cancel" ? t("working") : t("billing_cancel_confirm_cta")}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}


export function BillingSuccess() {
  const [params] = useSearchParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/billing/session/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
      } catch {}
      if (tries < 15) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen grid place-items-center p-8">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card p-10 max-w-md w-full text-center" data-testid="billing-success">
        {status === "checking" && (
          <>
            <Loader2 size={28} className="mx-auto animate-spin text-[hsl(var(--primary))]" />
            <div className="font-display text-[22px] mt-4 tracking-tight text-[hsl(var(--text-strong))]">{t("billing_confirming")}</div>
            <div className="text-[13px] text-[hsl(var(--text-muted))] mt-2">{t("billing_confirming_hint")}</div>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-14 h-14 rounded-full bg-[hsl(var(--success-soft))] text-[hsl(var(--success))] grid place-items-center mx-auto"><CheckCircle2 size={26} /></div>
            <div className="font-display text-[24px] mt-4 tracking-tight text-[hsl(var(--text-strong))]">{t("billing_paid_title")}</div>
            <div className="text-[13px] text-[hsl(var(--text-muted))] mt-2">{t("billing_paid_body")}</div>
            <button onClick={() => navigate("/billing")} className="btn btn-primary mt-6" data-testid="billing-back">{t("billing_back")}</button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <div className="w-14 h-14 rounded-full bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] grid place-items-center mx-auto"><XCircle size={26} /></div>
            <div className="font-display text-[22px] mt-4 tracking-tight text-[hsl(var(--text-strong))]">{t("billing_confirm_failed")}</div>
            <button onClick={() => navigate("/billing")} className="btn btn-primary mt-6">{t("billing_back")}</button>
          </>
        )}
      </motion.div>
    </div>
  );
}


export function BillingCancel() {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen grid place-items-center p-8">
      <div className="card p-10 max-w-md w-full text-center" data-testid="billing-cancel">
        <div className="w-14 h-14 rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))] grid place-items-center mx-auto"><XCircle size={26} /></div>
        <div className="font-display text-[22px] mt-4 tracking-tight text-[hsl(var(--text-strong))]">{t("billing_cancel_title")}</div>
        <div className="text-[13px] text-[hsl(var(--text-muted))] mt-2">{t("billing_cancel_body")}</div>
        <button onClick={() => navigate("/billing")} className="btn btn-primary mt-6">{t("billing_back")}</button>
      </div>
    </div>
  );
}
